import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Copy, MessageSquare, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'

interface Props {
  title: string
  text: string
  conversationId: string | null
  onClose: () => void
}

export default function AiResultModal({ title, text, conversationId, onClose }: Props) {
  const { t } = useTranslation('common')
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      /* noop */
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl surface-deep p-5 animate-slideUp max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold t-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted"
            aria-label={t('actions.close')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 prose prose-sm prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
          >
            <Copy size={14} /> {t('actions.copy')}
          </button>
          {conversationId && (
            <Link
              to={`/ai?conversation=${conversationId}`}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              <MessageSquare size={14} /> {t('aiResultModal.openInChat')}
            </Link>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
